import {
  EdgeStyleBase,
  SvgVisual,
  IRenderContext,
  IEdge,
  type Visual,
  PolylineEdgeStyle,
  Stroke,
  Fill
} from '@yfiles/yfiles';

/**
 * Custom edge style that displays an animated dot moving along the edge path.
 * The dot continuously animates from source to target, creating a flow visualization.
 */
export class DotEdgeStyle extends EdgeStyleBase {
  private readonly delegateStyle: PolylineEdgeStyle;
  public cssClass: string = '';

  constructor(
    private readonly baseStroke: string = '#08f',
    private readonly baseStrokeWidth: number = 3,
    private readonly dotColor: string = '#08f',
    private readonly dotRadius: number = 4,
    private readonly basePixelsPerSecond: number = 600
  ) {
    super();
    // Use PolylineEdgeStyle as delegate for proper cropping
    this.delegateStyle = new PolylineEdgeStyle({
      stroke: new Stroke(Fill.from(baseStroke), baseStrokeWidth),
      smoothingLength: 10
    });
  }

  /**
   * Gets the stroke width of this edge style.
   * Used by layout algorithms that need edge thickness information.
   */
  get strokeWidth(): number {
    return this.baseStrokeWidth;
  }

  createVisual(context: IRenderContext, edge: IEdge): SvgVisual {
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    
    // Apply CSS class for hover effects
    if (this.cssClass) {
      g.setAttribute('class', this.cssClass);
    }

    // Use delegate style to render the edge with proper cropping
    const delegateVisual = this.delegateStyle.renderer.getVisualCreator(edge, this.delegateStyle).createVisual(context);
    if (delegateVisual instanceof SvgVisual) {
      g.appendChild(delegateVisual.svgElement);
    }

    const dotCount = this.calculateDotCount(edge);

    // Create dots with staggered phase offsets
    for (let i = 0; i < dotCount; i++) {
      const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      dot.setAttribute('r', String(this.dotRadius));
      dot.setAttribute('fill', this.dotColor);
      dot.style.setProperty('visibility', 'hidden');
      g.appendChild(dot);

      // Phase offset: evenly distribute dots along the animation cycle
      const phaseOffset = i / dotCount;
      this.startAnimationLoop(dot, edge, phaseOffset);
    }

    return new SvgVisual(g);
  }

  /**
   * Calculates the number of dots to display based on source node's Volume percentage.
   * Volume 0% -> 1 dot, Volume 100% -> 4 dots
   */
  private calculateDotCount(edge: IEdge): number {
    const sourceVolume = edge.sourceNode?.tag?.percentage?.VolumePercentage;
    let dotCount = 1;
    if (typeof sourceVolume === 'number' && sourceVolume >= 0 && sourceVolume <= 100) {
      dotCount = Math.max(1, Math.round(1 + (3 * sourceVolume / 100))); // 1 to 4 dots
    }
    return dotCount;
  }

  /**
   * Converts animation time progress to distance along the edge path.
   * 
   * The animation runs on a time-based loop, but we need to position dots at specific
   * pixel distances along the path. This is complex because:
   * 
   * 1. The dot moves at normal speed for most of the path
   * 2. The dot slows down dramatically in the last 80 pixels
   * 3. Different target nodes have different slowdown multipliers based on health
   * 
   * Without this conversion, time progress would map linearly to distance, giving
   * constant speed everywhere. Instead, we calculate:
   * - How much time is spent in the normal segment vs slow segment
   * - Map time progress to the appropriate distance based on which segment we're in
   * 
   * This creates the visual effect: fast movement → dramatic slowdown at the end
   */
  private calculateDistanceAlongEdge(
    timeProgress: number,
    pathLength: number,
    normalSegmentLength: number,
    slowSegmentLength: number,
    normalSegmentTime: number,
    lastSegmentTime: number
  ): number {
    const totalDuration = normalSegmentTime + lastSegmentTime;
    const normalTimeRatio = totalDuration > 0 ? normalSegmentTime / totalDuration : 0;

    if (normalSegmentLength <= 0) {
      // Entire path is slow segment (path ≤ 80px)
      return timeProgress * slowSegmentLength;
    } else if (timeProgress <= normalTimeRatio) {
      // In normal segment (first part)
      return (timeProgress / normalTimeRatio) * normalSegmentLength;
    } else {
      // In slow segment (last 80px)
      const slowTimeProgress = (timeProgress - normalTimeRatio) / (1 - normalTimeRatio);
      return normalSegmentLength + slowTimeProgress * slowSegmentLength;
    }
  }

  private animateDot(
    dot: SVGElement,
    pathElement: SVGPathElement,
    pathLength: number,
    startTime: number,
    phaseOffset: number,
    normalSegmentLength: number,
    slowSegmentLength: number,
    normalSegmentTime: number,
    lastSegmentTime: number
  ): void {
    const totalDuration = (normalSegmentTime + lastSegmentTime) * 1000; // convert to milliseconds

    const animate = (currentTime: number) => {
      if (!dot.isConnected || dot.dataset.animationRunning !== 'true') {
        return; // Stop if element is removed or animation cancelled
      }

      const elapsed = currentTime - startTime;
      const rawProgress = totalDuration > 0 ? (elapsed % totalDuration) / totalDuration : 0;
      const timeProgress = (rawProgress + phaseOffset) % 1;

      const distance = this.calculateDistanceAlongEdge(
        timeProgress,
        pathLength,
        normalSegmentLength,
        slowSegmentLength,
        normalSegmentTime,
        lastSegmentTime
      );
      
      // Get point along the path
      const point = pathElement.getPointAtLength(distance);
      dot.setAttribute('transform', `translate(${point.x} ${point.y})`);
      
      // Show dot on first frame
      if (elapsed < 16 && dot.style.visibility === 'hidden') {
        dot.style.setProperty('visibility', 'visible');
      }

      // Continue animation and store frame ID
      (dot as any).__animationFrameId = requestAnimationFrame(animate);
    };

    dot.dataset.animationRunning = 'true';
    (dot as any).__animationFrameId = requestAnimationFrame(animate);
  }

  private startAnimationLoop(dot: SVGElement, edge: IEdge, phaseOffset: number = 0): void {
    // Cancel any existing animation
    const existingFrameId = (dot as any).__animationFrameId;
    if (existingFrameId) {
      cancelAnimationFrame(existingFrameId);
    }

    // Get the edge path for animation
    const geometry = edge.style.renderer.getPathGeometry(edge, edge.style);
    const path = geometry.getPath();
    
    if (!path) {
      return;
    }

    const pathElement = path.createSvgPath();
    const pathLength = pathElement.getTotalLength();
    const startTime = performance.now();

    // Slow down in the last N pixels of the path
    // Slowdown factor depends on target node health: worse health = bigger slowdown
    const slowPixels = 70;
    const targetHealth = edge.targetNode?.tag?.percentage?.TimePercentage;
    
    // Exponential slowdown: healthy nodes barely slow, unhealthy nodes slow dramatically
    // Health 100% -> multiplier ~1.0 (no slowdown)
    // Health 50%  -> multiplier ~0.32
    // Health 0%   -> multiplier 0.02
    let lastSegmentSpeedMultiplier = 1;
    if (typeof targetHealth === 'number' && targetHealth >= 0 && targetHealth <= 100) {
      const healthRatio = targetHealth / 100;
      lastSegmentSpeedMultiplier = 0.02 + 0.98 * Math.pow(healthRatio, 3);
    }

    const slowSegmentLength = Math.min(slowPixels, pathLength);
    const normalSegmentLength = Math.max(0, pathLength - slowSegmentLength);

    const normalSegmentTime = normalSegmentLength / this.basePixelsPerSecond;
    const lastSegmentTime =
      slowSegmentLength > 0
        ? slowSegmentLength / (this.basePixelsPerSecond * lastSegmentSpeedMultiplier)
        : 0;

    this.animateDot(
      dot,
      pathElement,
      pathLength,
      startTime,
      phaseOffset,
      normalSegmentLength,
      slowSegmentLength,
      normalSegmentTime,
      lastSegmentTime
    );
  }

  updateVisual(context: IRenderContext, oldVisual: Visual, edge: IEdge): SvgVisual {
    if (!(oldVisual instanceof SvgVisual)) {
      return this.createVisual(context, edge);
    }
    const g = oldVisual.svgElement as SVGGElement;

    // Update CSS class for hover effects
    const currentClass = g.getAttribute('class') || '';
    if (currentClass !== this.cssClass) {
      if (this.cssClass) {
        g.setAttribute('class', this.cssClass);
      } else {
        g.removeAttribute('class');
      }
    }

    // Update the delegate edge visual (first child)
    const delegateElement = g.firstChild as SVGElement;
    const delegateVisual = new SvgVisual(delegateElement);
    const updatedDelegateVisual = this.delegateStyle.renderer.getVisualCreator(edge, this.delegateStyle).updateVisual(context, delegateVisual);
    
    let pathChanged = false;
    if (updatedDelegateVisual instanceof SvgVisual && updatedDelegateVisual.svgElement !== delegateElement) {
      // Path structure changed, replace it
      g.replaceChild(updatedDelegateVisual.svgElement, delegateElement);
      pathChanged = true;
    }

    // Restart animations for all dots if path changed
    // Dots are all children after the first (delegate edge visual)
    const children = Array.from(g.children);
    const dots = children.slice(1) as SVGCircleElement[];
    const dotCount = dots.length;
    
    for (let i = 0; i < dotCount; i++) {
      const dot = dots[i];
      if (pathChanged || dot.dataset.animationRunning !== 'true') {
        dot.dataset.animationRunning = 'false';
        const phaseOffset = i / dotCount;
        this.startAnimationLoop(dot, edge, phaseOffset);
      }
    }

    return oldVisual;
  }
}

